import isPropValid from '@emotion/is-prop-valid';
import { type ReactElement, useContext } from 'react';
import {
  StyleSheetContext,
  StyleSheetManager as SCStyleSheetManager,
  __PRIVATE__,
} from 'styled-components';

import { IS_PRERENDER } from '#src/config.ts';

const { StyleSheet } = __PRIVATE__;

type SheetOptions = ConstructorParameters<typeof StyleSheet>[0];

const STYLED_COMPONENTS_LAYER = 'styled-components';

const layerRules = (rules: string | string[]) =>
  (Array.isArray(rules) ? rules : [rules]).map(
    rule => `@layer ${STYLED_COMPONENTS_LAYER}{${rule}}`
  );

class LayeredStyleSheet extends StyleSheet {
  insertRules(id: string, name: string, rules: string | string[]) {
    super.insertRules(id, name, layerRules(rules));
  }

  // The base implementation news up a plain StyleSheet, which would inject unlayered.
  reconstructWithOptions(options: SheetOptions, withNames = true) {
    return new LayeredStyleSheet(
      { ...this.options, ...options },
      this.gs,
      withNames ? this.names : undefined
    );
  }
}

const layeredStyleSheet = new LayeredStyleSheet();

function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  return true;
}

type Props = {
  children: ReactElement;
  /** Inject into another document, e.g. a picture-in-picture window head. */
  target?: HTMLElement;
};

export const StyleSheetManager = ({ children, target }: Props) => {
  const context = useContext(StyleSheetContext);

  return (
    <StyleSheetContext value={{ ...context, styleSheet: layeredStyleSheet }}>
      <SCStyleSheetManager
        disableCSSOMInjection={IS_PRERENDER}
        enableVendorPrefixes
        shouldForwardProp={shouldForwardProp}
        target={target}
      >
        {children}
      </SCStyleSheetManager>
    </StyleSheetContext>
  );
};
